import React, { useEffect, useMemo, useState } from 'react';
import styles from './CSS/AdminFeaturedCategories.module.css';
import { Pencil, Trash2, Image as ImageIcon, Link as LinkIcon, CheckSquare, XSquare } from 'lucide-react';
import { useFeaturedCategories, useCreateFeaturedCategory, useUpdateFeaturedCategory, useDeleteFeaturedCategory } from '../../../../hooks/admin/useAdminFeaturedCategories';

const initialForm = {
  title: '',
  subtitle: '',
  rank: 0,
  computer_link: '',
  mobile_link: '',
  computer_type: '',
  mobile_type: '',
  is_active: true,
  computerImage: null,
  mobileImage: null,
};

const AdminFeaturedCategoriesPage = () => {
  const [query, setQuery] = useState({ page: 1, limit: 10, search: '' });
  const [formData, setFormData] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [previews, setPreviews] = useState({ computer: null, mobile: null });
  const { data, isLoading, refetch } = useFeaturedCategories(query);
  const createMutation = useCreateFeaturedCategory();
  const updateMutation = useUpdateFeaturedCategory();
  const deleteMutation = useDeleteFeaturedCategory();

  const categories = useMemo(() => data?.categories || [], [data]);
  const pagination = data?.pagination;

  useEffect(() => { refetch(); }, [query.page, query.limit, refetch]);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFile = (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormData(prev => ({ ...prev, [key]: file }));
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreviews(prev => ({ ...prev, [key === 'computerImage' ? 'computer' : 'mobile']: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (key) => {
    setFormData(prev => ({ ...prev, [key]: null, [key === 'computerImage' ? 'removeComputerImage' : 'removeMobileImage']: true }));
    setPreviews(prev => ({ ...prev, [key === 'computerImage' ? 'computer' : 'mobile']: null }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditing(null);
    setPreviews({ computer: null, mobile: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, formData: fd });
    } else {
      await createMutation.mutateAsync(fd);
    }
    resetForm();
    refetch();
  };

  const onEdit = (item) => {
    setEditing(item);
    setFormData({
      title: item.title || '',
      subtitle: item.subtitle || '',
      rank: item.rank || 0,
      computer_link: item.computer_link || '',
      mobile_link: item.mobile_link || '',
      computer_type: item.computer_type || '',
      mobile_type: item.mobile_type || '',
      is_active: !!item.is_active,
      computerImage: null,
      mobileImage: null,
    });
    setPreviews({ computer: item.computer_image || null, mobile: item.mobile_image || null });
  };

  const onDelete = async (id) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}> 
        <h1>Featured Collections</h1>
      </div>

      <form className={styles.form} onSubmit={submit}>
        <div className={styles.grid}>
          <div className={styles.field}>
            <label>Title</label>
            <input name="title" value={formData.title} onChange={handleInput} placeholder="Title" />
          </div>
          <div className={styles.field}>
            <label>Subtitle</label>
            <input name="subtitle" value={formData.subtitle} onChange={handleInput} placeholder="Subtitle" />
          </div>
          <div className={styles.field}>
            <label>Rank</label>
            <input type="number" name="rank" value={formData.rank} onChange={handleInput} />
          </div>
          <div className={styles.field}>
            <label>Desktop Link</label>
            <input name="computer_link" value={formData.computer_link} onChange={handleInput} placeholder="/route-or-url" />
          </div>
          <div className={styles.field}>
            <label>Mobile Link</label>
            <input name="mobile_link" value={formData.mobile_link} onChange={handleInput} placeholder="/route-or-url" />
          </div>
          <div className={styles.field}>
            <label>Desktop Type</label>
            <input name="computer_type" value={formData.computer_type} onChange={handleInput} placeholder="e.g. ebook, audio" />
          </div>
          <div className={styles.field}>
            <label>Mobile Type</label>
            <input name="mobile_type" value={formData.mobile_type} onChange={handleInput} placeholder="e.g. ebook, audio" />
          </div>
          <div className={styles.fieldRow}>
            <label>Status</label>
            <div className={styles.checkboxRow}>
              <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInput} />
              <span>Active</span>
            </div>
          </div>
          <div className={styles.uploader}>
            <div className={styles.uploadBox}>
              <div className={styles.preview}>
                {previews.computer ? <img src={previews.computer} alt="desktop" /> : <ImageIcon />}
              </div>
              <label className={styles.uploadLabel}>Desktop Image</label>
              <input type="file" accept="image/*" onChange={(e) => handleFile(e, 'computerImage')} />
              {previews.computer && (
                <button type="button" className={styles.removeBtn} onClick={() => removeImage('computerImage')}>Remove</button>
              )}
            </div>
            <div className={styles.uploadBox}>
              <div className={styles.preview}>
                {previews.mobile ? <img src={previews.mobile} alt="mobile" /> : <ImageIcon />}
              </div>
              <label className={styles.uploadLabel}>Mobile Image</label>
              <input type="file" accept="image/*" onChange={(e) => handleFile(e, 'mobileImage')} />
              {previews.mobile && (
                <button type="button" className={styles.removeBtn} onClick={() => removeImage('mobileImage')}>Remove</button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={styles.primary} type="submit">{editing ? 'Update' : 'Create'}</button>
          {editing && (
            <button className={styles.secondary} type="button" onClick={resetForm}>Cancel</button>
          )}
        </div>
      </form>

      <div className={styles.listHeader}>
        <h2>Existing Featured Collections</h2>
        <div className={styles.listFilters}>
          <input placeholder="Search..." value={query.search} onChange={(e) => setQuery(prev => ({ ...prev, search: e.target.value }))} />
          <button onClick={() => refetch()}>Search</button>
        </div>
      </div>

      <div className={styles.table}>
        <div className={styles.thead}>
          <div>Rank</div>
          <div>Title</div>
          <div>Type</div>
          <div>Links</div>
          <div>Status</div>
          <div>Actions</div>
        </div>
        {isLoading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          categories.map((c) => (
            <div className={styles.trow} key={c.id}>
              <div>{c.rank}</div>
              <div>{c.title}</div>
              <div>{c.computer_type || '-'} / {c.mobile_type || '-'}</div>
              <div className={styles.links}>
                {c.computer_link ? <a href={c.computer_link} target="_blank" rel="noreferrer"><LinkIcon size={16} /> desktop</a> : '-'}
                {c.mobile_link ? <a href={c.mobile_link} target="_blank" rel="noreferrer"><LinkIcon size={16} /> mobile</a> : '-'}
              </div>
              <div>{c.is_active ? <CheckSquare color="#16a34a" /> : <XSquare color="#dc2626" />}</div>
              <div className={styles.rowActions}>
                <button className={styles.iconBtn} onClick={() => onEdit(c)}><Pencil size={16} /></button>
                <button className={styles.iconBtn} onClick={() => onDelete(c.id)}><Trash2 size={16} /></button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.pagination}>
        <button disabled={query.page <= 1} onClick={() => setQuery(prev => ({ ...prev, page: prev.page - 1 }))}>Prev</button>
        <span>{pagination?.currentPage || 1} / {pagination?.totalPages || 1}</span>
        <button disabled={pagination && query.page >= pagination.totalPages} onClick={() => setQuery(prev => ({ ...prev, page: prev.page + 1 }))}>Next</button>
      </div>
    </div>
  );
};

export default AdminFeaturedCategoriesPage;